// Apple Pencil text highlighter for the Quill editor.
// When a tool is selected, pen pointer events on the editor surface apply
// a background colour (or remove one) to the text underneath instead of
// performing a normal selection. Finger / mouse input is left untouched.

import Quill from 'quill';

export type HighlighterTool = 'off' | 'yellow' | 'green' | 'pink' | 'erase';

const COLORS: Record<Exclude<HighlighterTool, 'off' | 'erase'>, string> = {
  yellow: 'rgba(247, 208, 96, 0.55)',
  green: 'rgba(158, 206, 106, 0.55)',
  pink: 'rgba(247, 118, 142, 0.55)',
};

interface State {
  tool: HighlighterTool;
  active: boolean;
  pointerId: number | null;
  startIndex: number | null;
  lastIndex: number | null;
  quill: Quill | null;
}

const state: State = {
  tool: 'off',
  active: false,
  pointerId: null,
  startIndex: null,
  lastIndex: null,
  quill: null,
};

function caretFromPoint(x: number, y: number): { node: Node; offset: number } | null {
  // caretRangeFromPoint is supported on WebKit (iPadOS Safari) and Chromium.
  const doc = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof doc.caretRangeFromPoint === 'function') {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) return { node: r.startContainer, offset: r.startOffset };
  }
  if (typeof doc.caretPositionFromPoint === 'function') {
    const p = doc.caretPositionFromPoint(x, y);
    if (p) return { node: p.offsetNode, offset: p.offset };
  }
  return null;
}

function pointToQuillIndex(quill: Quill, x: number, y: number): number | null {
  const cp = caretFromPoint(x, y);
  if (!cp) return null;
  // Quill v2: static find returns the blot for a DOM node.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const Q = Quill as unknown as { find: (n: Node, bubble?: boolean) => any };
  const blot = Q.find(cp.node, true);
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (!blot) return null;
  try {
    const base = quill.getIndex(blot);
    return base + cp.offset;
  } catch {
    return null;
  }
}

function applyHighlight(quill: Quill, fromIdx: number, toIdx: number, tool: HighlighterTool) {
  if (tool === 'off') return;
  const start = Math.min(fromIdx, toIdx);
  const end = Math.max(fromIdx, toIdx);
  const len = end - start;
  if (len <= 0) return;
  const max = quill.getLength() - 1;
  const safeStart = Math.max(0, Math.min(start, max));
  const safeLen = Math.max(0, Math.min(len, max - safeStart));
  if (safeLen === 0) return;
  if (tool === 'erase') {
    quill.formatText(safeStart, safeLen, { background: false }, 'user');
  } else {
    quill.formatText(safeStart, safeLen, 'background', COLORS[tool], 'user');
  }
}

function isPenEvent(e: PointerEvent): boolean {
  return e.pointerType === 'pen';
}

function onPointerDown(e: PointerEvent) {
  if (state.tool === 'off') return;
  if (!isPenEvent(e)) return;
  const q = state.quill;
  if (!q) return;
  const idx = pointToQuillIndex(q, e.clientX, e.clientY);
  if (idx === null) return;
  e.preventDefault();
  e.stopPropagation();
  (q.root as HTMLElement).setPointerCapture?.(e.pointerId);
  state.active = true;
  state.pointerId = e.pointerId;
  state.startIndex = idx;
  state.lastIndex = idx;
  // Clear any text selection that the pen tap may have created.
  const sel = window.getSelection();
  if (sel) sel.removeAllRanges();
}

function onPointerMove(e: PointerEvent) {
  if (!state.active || state.pointerId !== e.pointerId) return;
  const q = state.quill;
  if (!q || state.startIndex === null) return;
  e.preventDefault();
  // Fall back to last known position if pointer is over non-text area.
  const idx = pointToQuillIndex(q, e.clientX, e.clientY) ?? state.lastIndex ?? state.startIndex;
  state.lastIndex = idx;
  applyHighlight(q, state.startIndex, idx, state.tool);
  const sel = window.getSelection();
  if (sel) sel.removeAllRanges();
}

function onPointerUp(e: PointerEvent) {
  if (!state.active || state.pointerId !== e.pointerId) return;
  const q = state.quill;
  if (q && state.startIndex !== null && state.lastIndex !== null) {
    applyHighlight(q, state.startIndex, state.lastIndex, state.tool);
  }
  state.active = false;
  state.pointerId = null;
  state.startIndex = null;
  state.lastIndex = null;
  const sel = window.getSelection();
  if (sel) sel.removeAllRanges();
}

const TOOL_LABELS: Record<HighlighterTool, string> = {
  off: '', yellow: 'Yellow', green: 'Green', pink: 'Pink', erase: 'Erasing',
};
const TINT_CLASSES = ['pencil-active-yellow', 'pencil-active-green', 'pencil-active-pink', 'pencil-active-erase'] as const;

export function setHighlighterTool(tool: HighlighterTool) {
  state.tool = tool;
  document.body.classList.toggle('pencil-mode', tool !== 'off');
  document.querySelectorAll('[data-hl-tool]').forEach((el) => {
    const t = (el as HTMLElement).dataset.hlTool as HighlighterTool;
    el.classList.toggle('active', t === tool);
  });

  // Mode label + section tint.
  const section = document.querySelector<HTMLElement>('.pencil-section');
  if (section) {
    TINT_CLASSES.forEach((c) => section.classList.remove(c));
    if (tool !== 'off') section.classList.add(`pencil-active-${tool}`);
  }
  const label = document.getElementById('pencil-mode-label');
  if (label) {
    label.textContent = TOOL_LABELS[tool];
    label.classList.toggle('visible', tool !== 'off');
  }

  // CSS hints: suppress selection, show appropriate cursor.
  const root = state.quill?.root as HTMLElement | undefined;
  if (root) {
    root.classList.toggle('pencil-active', tool !== 'off');
    root.classList.toggle('pencil-highlight', tool !== 'off' && tool !== 'erase');
    root.classList.toggle('pencil-erase', tool === 'erase');
  }
}

export function getHighlighterTool(): HighlighterTool {
  return state.tool;
}

export function installPencilHighlighter(quill: Quill) {
  state.quill = quill;
  const root = quill.root as HTMLElement;
  // Use capture so we beat Quill's own selection logic.
  root.addEventListener('pointerdown', onPointerDown, true);
  root.addEventListener('pointermove', onPointerMove, true);
  root.addEventListener('pointerup', onPointerUp, true);
  root.addEventListener('pointercancel', onPointerUp, true);
}
